import {
  LogViewerContext,
  LogViewerToolbarContext
} from '@patternfly/react-log-viewer';
import { useContext, useEffect, useMemo } from 'react';

import { searchForKeyword } from './searchForKeyword';

/**
 * Bridges the controlled `searchTerm` from the parent into PatternFly's
 * `LogViewer` context so that matching keywords are highlighted in the log
 * output.
 *
 * Memoizes the search result against `(searchTerm, parsedData.length)` so
 * that polling-driven `parsedData` replacements (the logs hook re-fetches
 * every few seconds while a generation runs) don't run a fresh regex sweep
 * on every parent render.
 */
export const LogViewerSearchSync = ({ searchTerm }: { searchTerm: string }) => {
  const {
    setSearchedInput,
    setSearchedWordIndexes,
    setCurrentSearchedItemCount
  } = useContext(LogViewerToolbarContext);
  const { parsedData } = useContext(LogViewerContext);

  const linesLength = parsedData.length;

  const foundIndexes = useMemo(() => {
    if (!searchTerm) {
      return null;
    }
    return searchForKeyword(searchTerm, parsedData, linesLength);
  }, [searchTerm, parsedData, linesLength]);

  useEffect(() => {
    if (searchTerm && foundIndexes) {
      setSearchedWordIndexes(foundIndexes);
      setSearchedInput(searchTerm);
      if (foundIndexes.length > 0 && foundIndexes[0].rowIndex !== -1) {
        setCurrentSearchedItemCount(0);
      }
    } else {
      setSearchedInput('');
      setSearchedWordIndexes([]);
      setCurrentSearchedItemCount(0);
    }
  }, [
    searchTerm,
    foundIndexes,
    setSearchedInput,
    setSearchedWordIndexes,
    setCurrentSearchedItemCount
  ]);

  return null;
};
